@if (loading()) {
  <div class="loading-overlay">
    <mat-spinner diameter="60"></mat-spinner>
    <p>Loading workflow request...</p>
  </div>
}

@if (error()) {
  <mat-card class="error-card">
    <mat-card-content>
      <mat-icon color="warn">error</mat-icon>
      <p>{{ error() }}</p>
      <button mat-raised-button (click)="router.navigate(['/securehome/workflows'])">Back to List</button>
    </mat-card-content>
  </mat-card>
}

@if (!loading() && !error() && request(); as r) {
  <div class="workflow-view-container">
    <mat-sidenav-container class="sidenav-container">
      <mat-sidenav #actionsSidenav mode="side" opened position="start" class="actions-sidenav">
        <app-entity-actions-sidenav
          title="Workflow Actions"
          [actions]="sidenavActions">
        </app-entity-actions-sidenav>
      </mat-sidenav>

      <mat-sidenav-content class="form-content">

        <!-- Header -->
        <div class="header-bar">
          <div class="header-left">
            <h2>{{ r.name || 'Workflow Request' }}</h2>
            <div class="header-sub">
              <span class="mono">{{ r.workflowNum }}</span>
              @if (r.requestor) {
                &middot; Requested by {{ getPersonName(r.requestor) }}
              }
            </div>
          </div>
          @if (r.status) {
            <mat-chip>{{ r.status }}</mat-chip>
          }
        </div>

        <!-- Phase Stepper (read-only) -->
        <mat-card class="detail-card stepper-card">
          <mat-card-content>
            <div class="phase-stepper">
              @for (phase of allPhases; track phase; let i = $index) {
                <div class="phase-step"
                     [class.completed]="i < currentPhaseIndex()"
                     [class.current]="i === currentPhaseIndex()">
                  <div class="phase-icon">
                    @if (i < currentPhaseIndex()) {
                      <mat-icon>check_circle</mat-icon>
                    } @else if (i === currentPhaseIndex()) {
                      <mat-icon>radio_button_checked</mat-icon>
                    } @else {
                      <mat-icon>radio_button_unchecked</mat-icon>
                    }
                  </div>
                  <div class="phase-label">{{ phaseShortName(phase) }}</div>
                </div>
                @if (i < allPhases.length - 1) {
                  <div class="phase-connector" [class.completed]="i < currentPhaseIndex()"></div>
                }
              }
            </div>
          </mat-card-content>
        </mat-card>

        <!-- Found in your records -->
        @if (matchedParty() || matchedLegalEntity() || matchedSite() || unmatchedPartyName()) {
          <mat-card class="detail-card matched-card">
            <mat-card-header>
              <mat-card-title>
                <mat-icon class="inline-icon">verified</mat-icon>
                Found in your records
              </mat-card-title>
            </mat-card-header>
            <mat-card-content>
              <div class="matched-grid">
                <div class="matched-row">
                  <span class="matched-label">Party / Vendor</span>
                  @if (matchedParty()) {
                    <a class="matched-link" href="javascript:void(0)" (click)="viewMatched(matchedParty())">
                      <mat-icon class="matched-check">check_circle</mat-icon>
                      {{ matchedParty()!.name }}
                      <mat-icon class="matched-arrow">chevron_right</mat-icon>
                    </a>
                  } @else if (unmatchedPartyName()) {
                    <span class="unmatched">
                      <mat-icon class="unmatched-icon">help_outline</mat-icon>
                      {{ unmatchedPartyName() }} <em>(no match found)</em>
                    </span>
                  } @else {
                    <span class="no-data">—</span>
                  }
                </div>
                <div class="matched-row">
                  <span class="matched-label">Legal Entity</span>
                  @if (matchedLegalEntity()) {
                    <a class="matched-link" href="javascript:void(0)" (click)="viewMatched(matchedLegalEntity())">
                      <mat-icon class="matched-check">check_circle</mat-icon>
                      {{ matchedLegalEntity()!.name }}
                      <mat-icon class="matched-arrow">chevron_right</mat-icon>
                    </a>
                  } @else {
                    <span class="no-data">—</span>
                  }
                </div>
                <div class="matched-row">
                  <span class="matched-label">Site</span>
                  @if (matchedSite()) {
                    <a class="matched-link" href="javascript:void(0)" (click)="viewMatched(matchedSite())">
                      <mat-icon class="matched-check">check_circle</mat-icon>
                      {{ matchedSite()!.name }}
                      <mat-icon class="matched-arrow">chevron_right</mat-icon>
                    </a>
                  } @else {
                    <span class="no-data">—</span>
                  }
                </div>
              </div>
            </mat-card-content>
          </mat-card>
        }

        <!-- AI extraction table -->
        @if (aiTableRows().length > 0) {
          <mat-card class="detail-card ai-card">
            <mat-card-header>
              <mat-card-title>
                <mat-icon class="inline-icon">psychology</mat-icon>
                What the AI found
              </mat-card-title>
            </mat-card-header>
            <mat-card-content>
              <table mat-table [dataSource]="aiTableRows()" class="ai-table">
                <ng-container matColumnDef="label">
                  <th mat-header-cell *matHeaderCellDef>Field</th>
                  <td mat-cell *matCellDef="let row" class="ai-label-cell">{{ row.label }}</td>
                </ng-container>
                <ng-container matColumnDef="value">
                  <th mat-header-cell *matHeaderCellDef>Value</th>
                  <td mat-cell *matCellDef="let row" class="ai-value-cell">
                    {{ row.value }}
                    @if (row.label === 'File' && r.s3FileUrl) {
                      <button mat-stroked-button class="view-file-btn" (click)="viewFile()">
                        <mat-icon>open_in_new</mat-icon>
                        View
                      </button>
                    }
                  </td>
                </ng-container>
                <tr mat-header-row *matHeaderRowDef="['label', 'value']"></tr>
                <tr mat-row *matRowDef="let row; columns: ['label', 'value']"></tr>
              </table>
            </mat-card-content>
          </mat-card>
        }

        <!-- Document Review: approved provisions vs. the uploaded document -->
        @if (showDocumentReview()) {
          <mat-card class="detail-card review-card">
            <mat-card-header>
              <mat-card-title>
                <mat-icon class="inline-icon">fact_check</mat-icon>
                Document Review
              </mat-card-title>
              <mat-card-subtitle>
                How this document measures up against your approved provisions
              </mat-card-subtitle>
            </mat-card-header>
            <mat-card-content>
              @if (!reviewAppliesToDocType()) {
                <div class="review-empty">
                  <mat-icon>fact_check</mat-icon>
                  <p>Document review compares a contract against your approved provisions.
                    The AI classified this document as {{ reviewDocTypeLabel() }}, and approved
                    provisions are scoped by contract type — so there is nothing meaningful to
                    review it against.</p>
                </div>
              } @else {
              <div class="review-actions">
                @if (reviewAnalyzedAt() && !reviewRunning()) {
                  <span class="review-timestamp">Reviewed {{ reviewAnalyzedAtDisplay() }}</span>
                }
                @if (reviewScopeLabel() && !reviewRunning()) {
                  <span class="review-scope">{{ reviewScopeLabel() }}</span>
                }
                <span class="review-spacer"></span>
                <button mat-stroked-button
                        (click)="runDocumentReview()"
                        [disabled]="reviewRunning() || reviewProvisions().length === 0">
                  <mat-icon>refresh</mat-icon>
                  {{ reviewComparisons().length > 0 ? 'Re-review' : 'Review' }}
                </button>
              </div>

              @if (reviewRunning()) {
                <div class="review-empty">
                  <mat-spinner diameter="40"></mat-spinner>
                  <p>{{ reviewStatus() || 'Reviewing...' }}</p>
                </div>
              } @else if (reviewComparisons().length > 0) {
                <app-provision-review-table [comparisons]="reviewComparisons()"></app-provision-review-table>
                @if (reviewError()) {
                  <p class="review-error-inline">
                    <mat-icon color="warn">error</mat-icon>
                    {{ reviewError() }}
                  </p>
                }
              } @else if (reviewError()) {
                <div class="review-empty">
                  <mat-icon color="warn">error</mat-icon>
                  <p>{{ reviewError() }}</p>
                </div>
              } @else if (!reviewProvisionsLoaded()) {
                <div class="review-empty">
                  <mat-spinner diameter="40"></mat-spinner>
                  <p>Loading your approved provisions...</p>
                </div>
              } @else if (reviewProvisions().length === 0) {
                <div class="review-empty">
                  <mat-icon>fact_check</mat-icon>
                  <p>No approved provisions apply to this document, so there is nothing to review
                    it against. Provisions become part of this review once they are marked
                    approved with their action completed.</p>
                </div>
              } @else {
                <div class="review-empty">
                  <mat-icon>fact_check</mat-icon>
                  <p>Not reviewed yet. Click <strong>Review</strong> to compare this document
                    against {{ reviewScopeLabel() }}.</p>
                </div>
              }
              }
            </mat-card-content>
          </mat-card>
        }

        <!-- Request Details -->
        <mat-card class="detail-card">
          <mat-card-header>
            <mat-card-title>Request Details</mat-card-title>
          </mat-card-header>
          <mat-card-content>
            <div class="detail-grid">
              <div class="detail-item">
                <span class="label">Phase</span>
                <span class="value">{{ r.phase || '—' }}</span>
              </div>
              <div class="detail-item">
                <span class="label">Document Type</span>
                <span class="value">{{ r.documentType || '—' }}</span>
              </div>
              <div class="detail-item">
                <span class="label">Transaction Type</span>
                <span class="value">{{ r.transactionType || '—' }}</span>
              </div>
              <div class="detail-item">
                <span class="label">Priority</span>
                <span class="value">{{ r.priority || '—' }}</span>
              </div>
              <div class="detail-item">
                <span class="label">Requestor</span>
                <span class="value">{{ getPersonName(r.requestor) }}</span>
              </div>
              <div class="detail-item">
                <span class="label">Assigned To</span>
                <span class="value">{{ getPersonName(r.assignedTo) }}</span>
              </div>
              <div class="detail-item">
                <span class="label">Date Requested</span>
                <span class="value">{{ r.dateRequested | date:'mediumDate' }}</span>
              </div>
              <div class="detail-item">
                <span class="label">Date Completed</span>
                <span class="value">{{ (r.dateCompleted | date:'mediumDate') || '—' }}</span>
              </div>
            </div>
          </mat-card-content>
        </mat-card>

        <!-- Notes -->
        @if (r.requestNotes) {
          <mat-card class="detail-card">
            <mat-card-header>
              <mat-card-title>Request Notes</mat-card-title>
            </mat-card-header>
            <mat-card-content>
              <p class="notes-text">{{ r.requestNotes }}</p>
            </mat-card-content>
          </mat-card>
        }
      </mat-sidenav-content>
    </mat-sidenav-container>
  </div>
}
